// ============================================================
// PAID TRIAL FORM — Fix #6: loads existing trial and UPDATES it.
// ============================================================
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, Check } from 'lucide-react';
import { api, TRIAL_CHECKS, TRIAL_RESULTS, fmtSaved } from './constants';

export function TrialForm({ candidateId, navigate }) {
  const base = { candidate_id: candidateId, trial_date: new Date().toISOString().split('T')[0], trial_length: '3-4 hours', paid: true, trial_result: '', trial_notes: '' };
  TRIAL_CHECKS.forEach(t => { base[t.key] = null; });
  const [form, setForm] = useState(base);
  const [existingId, setExistingId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api(`/api/recruitment/candidates/${candidateId}`).then(c => {
      if (!active) return;
      const t = c.paid_trials && c.paid_trials[0];
      if (t) {
        setForm(prev => ({ ...prev, ...t, candidate_id: candidateId }));
        setExistingId(t.id);
        setSavedAt(t.updated_at || t.created_at);
      }
    }).catch(() => {}).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [candidateId]);

  const toggle = (key) => setForm(f => ({ ...f, [key]: f[key] === true ? false : true }));
  const checked = TRIAL_CHECKS.filter(t => form[t.key] === true).length;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      if (existingId) {
        const res = await api(`/api/recruitment/trials/${existingId}`, { method: 'PATCH', body: JSON.stringify(payload) });
        setSavedAt(res.updated_at || new Date().toISOString());
      } else {
        const res = await api('/api/recruitment/trials', { method: 'POST', body: JSON.stringify(payload) });
        setExistingId(res.id);
        setSavedAt(res.updated_at || res.created_at || new Date().toISOString());
      }
      toast.success('Trial saved');
      navigate('candidate', candidateId);
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  if (loading) return <div className="h-40 bg-muted rounded-2xl animate-skeleton" />;

  return (
    <div data-testid="trial-form">
      <button className="ch-back" onClick={() => navigate('candidate', candidateId)}><ArrowLeft size={16} /> Back</button>
      <h1 className="text-xl font-bold mb-1">Paid Work Trial</h1>
      <p className="text-sm text-muted-foreground">{checked}/{TRIAL_CHECKS.length} observations recorded</p>
      {savedAt && <p className="text-xs text-muted-foreground mb-5 mt-1 flex items-center gap-1"><Check size={12} className="text-[hsl(var(--success))]" /> Last saved {fmtSaved(savedAt)}</p>}
      {!savedAt && <div className="mb-5" />}
      <div className="ps-card-static p-6 mb-6">
        <div className="space-y-2">
          {TRIAL_CHECKS.map(t => (
            <button key={t.key} onClick={() => toggle(t.key)} className={`w-full flex items-center gap-3 p-3 rounded-xl text-left text-sm transition-colors ${form[t.key] === true ? 'bg-[hsl(142_40%_95%)]' : form[t.key] === false ? 'bg-[hsl(0_80%_97%)]' : 'bg-muted/30'}`}>
              <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${form[t.key] === true ? 'bg-[hsl(var(--success))] border-[hsl(var(--success))] text-white' : form[t.key] === false ? 'bg-destructive border-destructive text-white' : 'border-input'}`}>
                {form[t.key] === true ? '✓' : form[t.key] === false ? '✗' : ''}
              </span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="ps-card-static p-6">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Result and Payment</p>
        <div className="rec-field"><label className="rec-label">Trial Result</label><select value={form.trial_result} onChange={e => setForm(f => ({ ...f, trial_result: e.target.value }))} className="rec-input"><option value="">Select...</option>{TRIAL_RESULTS.map(r => <option key={r}>{r}</option>)}</select></div>
        <div className="rec-field mt-4"><label className="rec-label">Notes</label><textarea value={form.trial_notes || ''} onChange={e => setForm(f => ({ ...f, trial_notes: e.target.value }))} className="rec-input rec-textarea" rows={4} /></div>

        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-6 mb-3">Payment Details</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rec-field"><label className="rec-label">Trial Paid?</label><select value={form.paid ? 'yes' : 'no'} onChange={e => setForm(f => ({ ...f, paid: e.target.value === 'yes' }))} className="rec-input"><option value="yes">Yes</option><option value="no">Not yet</option></select></div>
          <div className="rec-field"><label className="rec-label">Amount Paid ($)</label><input type="number" value={form.payment_amount || ''} onChange={e => setForm(f => ({ ...f, payment_amount: parseFloat(e.target.value) || 0 }))} className="rec-input" placeholder="e.g. 120" /></div>
          <div className="rec-field"><label className="rec-label">Payment Date</label><input type="date" value={form.payment_date || ''} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} className="rec-input" /></div>
          <div className="rec-field"><label className="rec-label">Payment Method</label><select value={form.payment_method || ''} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))} className="rec-input"><option value="">Select...</option><option>Bank Transfer</option><option>Cash</option><option>Other</option></select></div>
        </div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-4 mb-2">Bank Details (if needed)</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rec-field"><label className="rec-label">Account Name</label><input value={form.bank_account_name || ''} onChange={e => setForm(f => ({ ...f, bank_account_name: e.target.value }))} className="rec-input" /></div>
          <div className="rec-field"><label className="rec-label">BSB</label><input value={form.bank_bsb || ''} onChange={e => setForm(f => ({ ...f, bank_bsb: e.target.value }))} className="rec-input" placeholder="000-000" /></div>
          <div className="rec-field"><label className="rec-label">Account Number</label><input value={form.bank_account_number || ''} onChange={e => setForm(f => ({ ...f, bank_account_number: e.target.value }))} className="rec-input" /></div>
        </div>
        <button onClick={save} disabled={saving} className="mt-6 px-6 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }} data-testid="trial-save-btn">{saving ? 'Saving...' : 'Save Trial'}</button>
      </div>
    </div>
  );
}
